import http from "@shared/api/http";
import i18n from "@shared/i18n/i18n";

const getNotificationsUrl = (suffix = "") =>
  `/:locale/react/:token/notifications${suffix}`;

// Create a notification for requesting a new program
export const createProgramRequestNotification = async (programId, message) => {
  try {
    const response = await http.post(
      getNotificationsUrl(),
      {
        notification: {
          notification_type: 'program_request',
          title: i18n.t('notifications.program_request.title'),
          message: i18n.t('notifications.program_request.message', { programId, message }),
          // The therapist app re-renders the text from this, in the therapist's language.
          metadata: { program_id: programId, request_message: message }
        }
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error creating program request notification:", error);
    throw error;
  }
};

// Create a notification when program is completed
export const createProgramCompletedNotification = async (programId, programName) => {
  try {
    const response = await http.post(
      getNotificationsUrl(),
      {
        notification: {
          notification_type: 'program_completed',
          title: i18n.t('notifications.program_completed.title'),
          message: i18n.t('notifications.program_completed.message', { programId, programName }),
          metadata: { program_id: programId, program_name: programName }
        }
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error creating program completed notification:", error);
    throw error;
  }
};

// Create a notification when feedback is sent
export const createProgramFeedbackNotification = async (programId, programName, feedback) => {
  try {
    const response = await http.post(
      getNotificationsUrl(),
      {
        notification: {
          notification_type: 'program_feedback',
          title: i18n.t('notifications.program_feedback.title'),
          message: i18n.t('notifications.program_feedback.message', { programId, programName }),
          metadata: { program_id: programId, program_name: programName }
        }
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error creating program feedback notification:", error);
    throw error;
  }
};

// Check if user can request a new program (backend validation)
export const canRequestNewProgram = async () => {
  try {
    const response = await http.get(
      getNotificationsUrl("/can_request_program"),
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error checking program request eligibility:", error);
    // Fallback to allowing request if backend check fails
    return { can_request: true, days_until_next: 0 };
  }
};

// Send subscription contact email to therapist (no notification created)
export const createSubscriptionContactNotification = async (programId, message, therapistEmail) => {
  try {
    const response = await http.post(
      getNotificationsUrl("/send_subscription_contact_email"),
      {
        program_id: programId,
        message: message,
        therapist_email: therapistEmail
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error sending subscription contact email:", error);
    throw error;
  }
};

// Milestones the practice should hear about: one notification per mark a
// saved session just reached (first full week, halfway, finished). The
// backend has to know the type; until it does the request is refused, which
// must never hold up the patient — failures are logged and swallowed.
export const MILESTONE_NOTIFICATION_IDS = ["first_week", "halfway", "completed"];

export const createMilestoneNotification = async (programId, programName, milestone) => {
  const response = await http.post(
    getNotificationsUrl(),
    {
      notification: {
        notification_type: "milestone_reached",
        title: i18n.t(`gamification.badges.${milestone}.name`),
        message: `${programName}: ${i18n.t(`gamification.badges.${milestone}.criterion`)}`,
        // The therapist app re-renders the text from this, in the therapist's language.
        metadata: { program_id: programId, program_name: programName, milestone },
      },
    },
    {
      headers: {
        "Content-Type": "application/json",
      },
    }
  );
  return response.data;
};

export const notifyReachedMilestones = (programId, programName, steps = []) => {
  steps
    .filter((step) => MILESTONE_NOTIFICATION_IDS.includes(step.id))
    .forEach((step) => {
      createMilestoneNotification(programId, programName, step.id).catch((error) =>
        console.error("Error creating milestone notification:", error)
      );
    });
};
